// Shared timeline-layout engine used by both the Class Timetable and
// My Timetable screens. Normalizes periods from either API shape into
// TimelinePeriod, then computes pixel positions for a 1-24 hour vertical grid.

export interface TimelinePeriod {
  periodId: string;
  periodStart: string; // HH:mm
  periodEnd: string;   // HH:mm
  title: string;       // subject / break label
  subtitle: string | null; // staff name / class name, null for break-recess
  isBreak: boolean;
  top?: number;
  height?: number;
  left?: number;
  width?: number;
}

export interface TimelineMetrics {
  periods: TimelinePeriod[];
  hourTops: number[]; // pixel top offset for each hour 0..24
  totalHeight: number;
}

function toMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
}

// Computes adaptive per-hour heights (an hour containing overlapping periods
// grows to fit them) and assigns each period a top/height/left/width so
// concurrent periods stack side-by-side instead of overlapping visually.
export function calculateTimelineMetrics(periods: TimelinePeriod[], hourHeight = 80, gap = 10): TimelineMetrics {
  const sorted = [...periods].sort((a, b) => toMinutes(a.periodStart) - toMinutes(b.periodStart));

  // Group overlapping periods into clusters so we can lay out columns.
  const clusters: TimelinePeriod[][] = [];
  let currentCluster: TimelinePeriod[] = [];
  let clusterEnd = -1;

  sorted.forEach(p => {
    const start = toMinutes(p.periodStart);
    const end = toMinutes(p.periodEnd);
    if (currentCluster.length && start >= clusterEnd) {
      clusters.push(currentCluster);
      currentCluster = [];
      clusterEnd = -1;
    }
    currentCluster.push(p);
    clusterEnd = Math.max(clusterEnd, end);
  });
  if (currentCluster.length) { clusters.push(currentCluster); }

  const minutesToY = (mins: number) => (mins / 60) * hourHeight;

  clusters.forEach(cluster => {
    const width = 100 / cluster.length;
    cluster.forEach((p, i) => {
      const start = toMinutes(p.periodStart);
      const end = toMinutes(p.periodEnd);
      p.top = minutesToY(start);
      p.height = Math.max(minutesToY(end - start) - gap, 36);
      p.left = width * i;
      p.width = width - (i < cluster.length - 1 ? (gap / 10) : 0);
    });
  });

  const hourTops: number[] = [];
  for (let h = 0; h <= 24; h++) { hourTops.push(h * hourHeight); }

  return { periods: sorted, hourTops, totalHeight: 24 * hourHeight };
}

// Maps an HH:mm time string to a pixel Y offset within the same grid used
// by calculateTimelineMetrics — used for both the "now" line and scroll-to.
export function getTimeTopOffset(time: string, hourHeight = 80): number {
  return (toMinutes(time) / 60) * hourHeight;
}

export function isPastPeriod(isToday: boolean, periodEnd: string, nowMinutes: number): boolean {
  return isToday && toMinutes(periodEnd) <= nowMinutes;
}

export function isCurrentPeriod(isToday: boolean, periodStart: string, periodEnd: string, nowMinutes: number): boolean {
  return isToday && toMinutes(periodStart) <= nowMinutes && nowMinutes <= toMinutes(periodEnd);
}

export function nowToMinutes(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

export interface WeekDay {
  date: Date;
  dayName: string;   // "Monday"
  shortLabel: string; // "Mon"
  dateLabel: string;  // "27"
  isToday: boolean;
}

export function getCurrentWeekDays(referenceDate: Date = new Date()): WeekDay[] {
  const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  const shortNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);
  const startOfWeek = new Date(today);
  startOfWeek.setDate(today.getDate() - today.getDay());

  const days: WeekDay[] = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(startOfWeek);
    d.setDate(startOfWeek.getDate() + i);
    days.push({
      date: d,
      dayName: dayNames[d.getDay()],
      shortLabel: shortNames[d.getDay()],
      dateLabel: String(d.getDate()),
      isToday: d.getTime() === today.getTime(),
    });
  }
  return days;
}
